import React from "react";
import DreamerBarChart from "@/components/DreamerCharts/DreamerBarChart";
import DreamerGaugeChart from "@/components/DreamerCharts/DreamerGaugeChart";
import DreamerLineChart from "@/components/DreamerCharts/DreamerLineChart";
import DreamerMultiLineChart from "@/components/DreamerCharts/DreamerMultiLineChart";
import DreamerPieChart from "@/components/DreamerCharts/DreamerPieChart";
import DreamerStackBarChart from "@/components/DreamerCharts/DreamerStackBarChart";
import DreamerAttachmentEmptyState, {
  DreamerAttachmentEmptyVariant
} from "@/components/global/Chat/components/dreamerAttachment/DreamerAttachmentEmptyState";
import { ChartKind } from "@/components/global/Chat/chartKind";
import {
  DreamerBarChartDataType,
  DreamerLineChartDataType,
  DreamerPieChartDataType,
  DreamerStackBarChartDataType,
  LineConfig,
  StackSegmentConfig
} from "@/components/DreamerCharts/types";

interface Props {
  chartKind: ChartKind;
  height: number | string;
  emptyVariant?: DreamerAttachmentEmptyVariant | null;
  isPercentage?: boolean;
  // Bar
  chartData?: DreamerBarChartDataType[];
  xAxisLabel?: string;
  yAxisLabel?: string;
  // Stacked bar
  stackedChartData?: DreamerStackBarChartDataType[];
  stackSegments?: StackSegmentConfig[];
  // Line
  lineChartData?: DreamerLineChartDataType[];
  lineConfigs?: LineConfig[];
  // Pie
  pieChartData?: DreamerPieChartDataType[];
  pieSize?: number;
  // Gauge
  gaugeValue?: number;
  gaugeTotal?: number;
  gaugeCenterValue?: string;
  gaugeCenterLabel?: string;
  gaugeTooltipLabel?: string;
  compact?: boolean;
}

const ChartBody: React.FC<Props> = ({
  chartKind,
  height,
  emptyVariant,
  isPercentage = false,
  chartData = [],
  xAxisLabel,
  yAxisLabel,
  stackedChartData = [],
  stackSegments = [],
  lineChartData = [],
  lineConfigs = [],
  pieChartData = [],
  pieSize = 120,
  gaugeValue = 0,
  gaugeTotal = 0,
  gaugeCenterValue,
  gaugeCenterLabel,
  gaugeTooltipLabel,
  compact = false
}) => {
  if (emptyVariant) {
    return <DreamerAttachmentEmptyState variant={emptyVariant} />;
  }

  switch (chartKind) {
    case ChartKind.StackedBar:
      return (
        <DreamerStackBarChart
          height={height}
          chartData={stackedChartData}
          segments={stackSegments}
          isPercentage={isPercentage}
          showLegend
          compact={compact}
        />
      );
    case ChartKind.Line:
      return (
        <DreamerLineChart
          height={height}
          chartData={lineChartData}
          lines={lineConfigs}
          isPercentage={isPercentage}
          showLegend={lineConfigs.length > 1}
          compact={compact}
        />
      );
    case ChartKind.MultiLine:
      return (
        <DreamerMultiLineChart
          height={height}
          chartData={lineChartData}
          lines={lineConfigs}
          isPercentage={isPercentage}
          compact={compact}
        />
      );
    case ChartKind.Pie:
      return (
        <DreamerPieChart
          size={pieSize}
          chartData={pieChartData}
          compact={compact}
        />
      );
    case ChartKind.Gauge:
      return (
        <DreamerGaugeChart
          value={gaugeValue}
          total={gaugeTotal}
          centerValue={gaugeCenterValue}
          centerLabel={gaugeCenterLabel}
          tooltipLabel={gaugeTooltipLabel}
          compact={compact}
        />
      );
    case ChartKind.Bar:
    default:
      return (
        <DreamerBarChart
          height={height}
          chartData={chartData}
          isPercentage={isPercentage}
          xAxisLabel={xAxisLabel}
          yAxisLabel={yAxisLabel}
          showLegend={false}
          compact={compact}
        />
      );
  }
};

export default ChartBody;
